<template>
  <div class="col-sm-12">
    <div class="card shadow-sm report-page">
      <!-- Filters Section -->
      <div class="dropdown-container">
        <!-- Distributor Dropdown -->
        <div class="dropdown-item">
          <label for="distributor">Select Distributor</label>
          <select v-model="filters.distributor" id="distributor" @change="onDistributorChange"
            :disabled="isDistributorLocked || loading">
            <option value="">Select Distributor</option>

            <option v-for="dist in distributors" :key="dist.profile_id ?? dist.id"
              :value="String(dist.profile_id ?? dist.id)">
              {{ dist.name ?? dist.first_name ?? dist.full_name ?? 'Unknown' }}
            </option>
          </select>
        </div>

        <!-- Date Field -->
        <div class="dropdown-item">
          <label for="date">Date</label>
          <input type="date" v-model="filters.date" id="date" :disabled="loading" />
        </div>

        <!-- Vehicle/Yard Dropdown -->
        <div class="dropdown-item">
          <label for="vehicle">Select Vehicle/Yard</label>
          <select
            id="vehicle"
            v-model="filters.vehicle"
            :disabled="vehiclesLoading || !filters.distributor || loading"
            :class="{ 'inactive-select': selectedVehicleIsInactive }"
          >
            <option
              v-for="option in vehicleOptions"
              :key="option.value"
              :value="option.value"
              :class="{ 'inactive-option': isInactiveOption(option) }"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="search-container">
          <button type="button" class="btn button-default" :disabled="loading" @click="search()">
            <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
            {{ loading ? "Searching..." : "Search" }}
          </button>
          <button type="button" class="btn button-export" :disabled="!hasData || loading" @click="exportToExcel">
            Export to Excel
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="pageLoading || loading" class="loading-container mt-4">
        <div class="loading-card">
          <div class="loading-spinner-wrapper">
            <i class="fa fa-spinner fa-spin loading-icon"></i>
          </div>
          <h6 class="loading-title">
            <span v-if="pageLoading">Loading page...</span>
            <span v-else>Loading Day End Report...</span>
          </h6>
          <p class="loading-subtitle">Please wait while the records are being retrieved.</p>
        </div>
      </div>

      <!-- Results Table -->
      <div v-if="!loading && reportData.length > 0" class="table-container">
        <table class="table table-responsive-sm">
          <thead>
            <tr>
              <th class="text-center">#</th>
              <th>DEALER NAME</th>
              <th>INVOICE NO</th>
              <th v-for="item in uniqueItems" :key="item.name" class="text-center">
                {{ String(item.name).toUpperCase() }}
              </th>
              <th v-for="payType in paymentTypes" :key="payType" class="text-end">
                {{ formatPaymentHeader(payType).toUpperCase() }}
              </th>
              <th class="text-end">TOTAL INVOICE AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in reportData" :key="index" :class="{ 'inactive-dealer-row': isInactiveDealer(row) }">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ row.dealer_name || "-" }}</td>
              <td>{{ row.invoices_no || row.invoice_no || "-" }}</td>

              <td v-for="item in uniqueItems" :key="item.name" class="text-center">
                {{ getItemQuantity(row, item.name) }}
              </td>

              <td v-for="payType in paymentTypes" :key="payType" class="text-end">
                {{ formatCurrency(getPaymentAmount(row, payType)) }}
              </td>
              <td class="text-end fw-bold">{{ formatCurrency(getInvoiceTotal(row)) }}</td>
            </tr>
          </tbody>

          <!-- Totals Row -->
          <tfoot>
            <tr>
              <td colspan="3" class="text-end"><strong>GRAND TOTAL:</strong></td>
              <td v-for="item in uniqueItems" :key="item.name" class="text-center">
                <strong>{{ getTotalForItem(item.name) }}</strong>
              </td>
              <td v-for="payType in paymentTypes" :key="payType" class="text-end">
                {{ formatCurrency(getTotalForPaymentType(payType)) }}
              </td>
              <td class="text-end">{{ formatCurrency(totalInvoiceAmount) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Modern Empty State -->
      <div v-else-if="!loading && searched && reportData.length === 0" class="no-data-container mt-4">
        <div class="no-data-card">
          <div class="no-data-icon-wrapper">
            <i class="fa fa-folder-open-o no-data-icon"></i>
          </div>
          <h5 class="no-data-title">No Data Found</h5>
          <p class="no-data-text">
            {{ searchErrorMessage || "There are no invoice records matching your selected filters." }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { useToast } from "vue-toastification";
import { fetchDistributorsList, fetchRepListInvoice, fetchDayEndReport } from "@/services/dataService";

export default {
  name: "DayEndReport",
  data() {
    return {
      profile_id: localStorage.getItem("profile_id") || null,
      userLabel: "",
      roleKey: "OTHER",
      isDistributorLocked: false,
      filters: {
        distributor: "",
        date: this.getDefaultDate(),
        vehicle: "all",
      },
      distributors: [],
      vehicles: [],
      reportData: [],
      uniqueItems: [],
      paymentTypes: [],
      loading: false,
      vehiclesLoading: false,
      searched: false,
      pageLoading: false,
      searchErrorMessage: "",
    };
  },

  computed: {
    vehicleOptions() {
      return [
        { value: "all", label: "All Invoices" },
        { value: "all_vehicles", label: "All Vehicle Invoices" },
        { value: "direct", label: "All Direct Invoices" },
        ...this.vehicles.map((v) => ({
          value: String(v.id),
          label: String(v.vehicle_no || "").trim(),
          active: v.is_active !== undefined ? v.is_active : undefined,
        })),
      ];
    },
    selectedVehicleIsInactive() {
      const selected = this.vehicleOptions.find(
        (opt) => String(opt.value) === String(this.filters.vehicle)
      );
      return this.isInactiveOption(selected);
    },
    hasData() {
      return Array.isArray(this.reportData) && this.reportData.length > 0;
    },
    totalInvoiceAmount() {
      if (!Array.isArray(this.reportData)) return 0;
      return this.reportData.reduce((sum, row) => sum + this.getInvoiceTotal(row), 0);
    },
  },

  created() {
    this.initRoleFromStorage();
    this.initPage();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      const options = { position: "top-right", timeout: 3000 };
      if (type === "error") {
        t.error(message, options);
      } else if (type === "warning") {
        t.warning(message, options);
      } else if (type === "info") {
        t.info(message, options);
      } else {
        t.success(message, options);
      }
    },

    getDefaultDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    // ✅ role detection
    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(labelFromStorage);
      const labelUpper = String(this.userLabel || "").toUpperCase().trim();

      if (
        labelUpper === "DISTRIBUTOR" ||
        labelUpper.startsWith("DISTRIBUTOR") ||
        labelUpper === "DIS" ||
        labelUpper.startsWith("DIS_")
      ) this.roleKey = "DIS";
      else if (labelUpper === "ASM" || labelUpper.startsWith("ASM_")) this.roleKey = "ASM";
      else if (labelUpper === "FSM" || labelUpper.startsWith("FSM_")) this.roleKey = "FSM";
      else this.roleKey = "OTHER";
    },

    async initPage() {
      this.pageLoading = true;
      await this.$nextTick(); // ✅ allow loader to paint

      try {
        await this.loadDistributors();
      } finally {
        this.pageLoading = false;
        if (!this.filters.vehicle) {
          this.filters.vehicle = "all";
        }
      }

      // Automatically load report with default data
      if (this.filters.distributor && this.filters.date) {
        await this.search(true);
      }
    },

    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    async loadDistributors() {
      try {
        const token = localStorage.getItem("token_set");
        const response = await fetchDistributorsList(this.profile_id, token);
        this.distributors = response?.data?.data || [];

        // ✅ Distributor login: prefill + lock + load vehicles
        if (this.isDistributorUser() && this.profile_id) {
          const matched = this.distributors.find(d => Number(d.profile_id ?? d.id) === Number(this.profile_id)) || this.distributors[0];
          this.filters.distributor = String(matched?.profile_id ?? matched?.id ?? "");
          this.isDistributorLocked = true;
          this.filters.vehicle = "all";

          if (this.filters.distributor) {
            await this.loadVehicles(this.filters.distributor);
          }
        } else {
          this.isDistributorLocked = false;
          // For other users (Admin/ASM/FSM), default to first distributor if available so report can auto-load
          if (!this.filters.distributor && this.distributors.length > 0) {
            this.filters.distributor = String(this.distributors[0]?.profile_id ?? this.distributors[0]?.id ?? "");
            this.filters.vehicle = "all";
            if (this.filters.distributor) {
              await this.loadVehicles(this.filters.distributor);
            }
          }
        }
      } catch (error) {
        console.error("Error loading distributors:", error);
        this.toast("error", "Failed to load distributors");
      }
    },

    onDistributorChange() {
      this.filters.vehicle = "all";
      this.vehicles = [];
      this.reportData = [];
      this.uniqueItems = [];
      this.searched = false;
      this.searchErrorMessage = "";

      if (this.filters.distributor) {
        this.loadVehicles(this.filters.distributor);
      }
    },

    async loadVehicles(distributor_profile_id) {
      this.vehiclesLoading = true;
      this.vehicles = [];
      try {
        const token = localStorage.getItem("token_set");
        const response = await fetchRepListInvoice(
          distributor_profile_id,
          distributor_profile_id,
          token,
          true,
        );
        let vehiclesData = response?.data?.data || [];

        this.vehicles = vehiclesData.map(v => ({
          ...v,
          id: v.profile_id ?? v.id ?? v.vehicle_id,
          vehicle_no: v.name ?? v.vehicle_no ?? "Unknown",
          is_active: v.is_active,
        }));
      } catch (error) {
        console.error("Error loading vehicles:", error);
        this.toast("error", "Failed to load vehicles");
      } finally {
        this.vehiclesLoading = false;
        if (!this.filters.vehicle) {
          this.filters.vehicle = "all";
        }
      }
    },

    async search(isInitial = false) {
      if (!this.filters.distributor) {
        this.toast("error", "Please select a distributor");
        return;
      }

      if (this.filters.vehicle === "" || this.filters.vehicle === null || this.filters.vehicle === undefined) {
        this.toast("error", "Please select a vehicle/yard option");
        return;
      }

      if (!this.filters.date) {
        this.toast("error", "Please select a date");
        return;
      }

      this.loading = true;
      this.searched = true;
      this.reportData = [];
      this.uniqueItems = [];
      this.paymentTypes = [];
      this.searchErrorMessage = "";

      try {
        const token = localStorage.getItem("token_set");

        let invoiceSource = "all";
        let vehicleId = "";

        if (this.filters.vehicle === "all_vehicles") {
          invoiceSource = "all_vehicles";
          vehicleId = "";
        } else if (this.filters.vehicle === "direct") {
          invoiceSource = "direct";
          vehicleId = "";
        } else if (this.filters.vehicle === "all" || this.filters.vehicle === "0") {
          invoiceSource = "all";
          vehicleId = "";
        } else {
          invoiceSource = "specific_vehicle";
          vehicleId = Number(this.filters.vehicle);
        }

        const params = {
          invoice_date: this.filters.date,
          distributor_profile_id: this.filters.distributor ? Number(this.filters.distributor) : null,
          invoice_source: invoiceSource,
          vehicle_id: vehicleId,
        };

        const response = await fetchDayEndReport(token, params);

        if (response?.data && response.data.status === false) {
          this.toast("error", response.data.message || "Failed to load report");
          this.reportData = [];
          return;
        }

        const invoiceList = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
          ? response.data
          : [];

        // Save raw API data as it is
        this.reportData = invoiceList;

        // Dynamic items and payment extraction (referencing invoiceSummeryForm)
        const itemMap = new Map();
        const paymentSet = new Set();
        const standardPaymentOrder = [
          "Cash",
          "Cheque",
          "Bank Transfer",
          "Advance",
          "Credit/Debit Card",
          "Mobile Payment",
          "Online Payment",
          "Credit",
        ];

        // If backend returns top-level items
        if (Array.isArray(response?.data?.items)) {
          response.data.items.forEach((i) => {
            const name = this.getItemName(i);
            if (name && !itemMap.has(name)) {
              itemMap.set(name, Number(i.variant_id || i.item_id) || 0);
            }
          });
        }

        // Collect from all invoice items & payment_summary
        this.reportData.forEach((inv) => {
          inv.items?.forEach((i) => {
            const name = this.getItemName(i);
            if (name && !itemMap.has(name)) {
              itemMap.set(name, Number(i.variant_id || i.item_id) || 0);
            }
          });

          if (inv.payment_summary && typeof inv.payment_summary === "object") {
            Object.keys(inv.payment_summary).forEach((k) => paymentSet.add(k));
          }
        });

        // Unique items sorted by variant_id if available, otherwise by name
        this.uniqueItems = Array.from(itemMap.keys())
          .sort((a, b) => {
            const idA = itemMap.get(a);
            const idB = itemMap.get(b);
            if (idA && idB) return idA - idB;
            return a.localeCompare(b);
          })
          .map((name) => ({
            name: name,
            variant_id: itemMap.get(name),
          }));

        // Dynamic payment types in standard order + extras
        if (paymentSet.size > 0) {
          this.paymentTypes = [
            ...standardPaymentOrder.filter((k) => paymentSet.has(k)),
            ...Array.from(paymentSet).filter((k) => !standardPaymentOrder.includes(k)),
          ];
        } else {
          this.paymentTypes = [];
        }

        if (this.reportData.length === 0) {
          const apiMsg = response?.data?.message || "";
          this.searchErrorMessage =
            apiMsg && !apiMsg.toLowerCase().includes("success")
              ? apiMsg
              : "No invoice records match your selected criteria. Please adjust your date or vehicle filter and search again.";
          if (!isInitial) {
            this.toast("error", "No data found for the selected criteria");
          }
        } else {
          this.searchErrorMessage = "";
          this.toast("success", response?.data?.message || `Report loaded successfully with ${this.reportData.length} invoices`);
        }
      } catch (error) {
        console.error("Error loading report:", error);
        const msg = error?.response?.data?.message || "Error loading report. Please try again.";
        this.searchErrorMessage = msg;
        this.toast("error", msg);
      } finally {
        this.loading = false;
      }
    },

    toNumber(value) {
      return parseFloat(String(value || "0").replace(/,/g, "")) || 0;
    },

    formatCurrency(amount) {
      return this.toNumber(amount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    isInactiveOption(option) {
      if (!option || option.active === undefined || option.active === null) return false;
      return (
        option.active === false ||
        Number(option.active) === 0 ||
        String(option.active) === "0"
      );
    },

    isInactiveDealer(row) {
      if (!row) return false;
      return (
        String(row.is_active) === "0" ||
        row.is_active === 0 ||
        row.is_active === false
      );
    },

    getItemName(item) {
      return item?.variant_name || item?.item_name || "";
    },

    getItemQty(item) {
      if (!item) return 0;
      const q = item.item_qty !== undefined ? item.item_qty : item.quantity;
      return parseFloat(q) || 0;
    },

    getItemQuantity(row, itemName) {
      if (!row || !Array.isArray(row.items)) return 0;
      const item = row.items.find(i => this.getItemName(i) === itemName);
      if (!item) return 0;
      const qty = item.item_qty !== undefined ? item.item_qty : item.quantity;
      return qty !== null && qty !== undefined && qty !== "" ? qty : 0;
    },

    getTotalForItem(itemName) {
      if (!Array.isArray(this.reportData)) return 0;
      return this.reportData.reduce((sum, row) => {
        const item = row.items?.find(i => this.getItemName(i) === itemName);
        return sum + this.getItemQty(item);
      }, 0);
    },

    formatPaymentHeader(name) {
      if (!name) return "";
      const upper = String(name).trim().toUpperCase();

      const exactMap = {
        "CASH": "CASH PAYMENTS",
        "CASH AMOUNT": "CASH PAYMENTS",
        "TOTAL CASH PAYMENTS": "CASH PAYMENTS",
        "CHEQUE": "CHEQUE PAYMENTS",
        "CHEQUE AMOUNT": "CHEQUE PAYMENTS",
        "TOTAL CHEQUE PAYMENTS": "CHEQUE PAYMENTS",
        "BANK TRANSFER": "BANK TRANSFER PAYMENTS",
        "BANK TRANSFER AMOUNT": "BANK TRANSFER PAYMENTS",
        "TOTAL BANK TRANSFER PAYMENTS": "BANK TRANSFER PAYMENTS",
        "ADVANCE": "ADVANCE PAYMENTS",
        "ADVANCE AMOUNT": "ADVANCE PAYMENTS",
        "TOTAL ADVANCE PAYMENTS": "ADVANCE PAYMENTS",
        "CREDIT/DEBIT CARD": "CREDIT/DEBIT CARD PAYMENTS",
        "CREDIT/DEBIT CARD AMOUNT": "CREDIT/DEBIT CARD PAYMENTS",
        "TOTAL CREDIT/DEBIT CARD PAYMENTS": "CREDIT/DEBIT CARD PAYMENTS",
        "MOBILE PAYMENT": "MOBILE PAYMENTS",
        "MOBILE PAYMENT AMOUNT": "MOBILE PAYMENTS",
        "TOTAL MOBILE PAYMENTS": "MOBILE PAYMENTS",
        "ONLINE PAYMENT": "ONLINE PAYMENTS",
        "ONLINE PAYMENT AMOUNT": "ONLINE PAYMENTS",
        "TOTAL ONLINE PAYMENTS": "ONLINE PAYMENTS",
        "CREDIT": "CREDIT AMOUNT",
        "CREDIT AMOUNT": "CREDIT AMOUNT",
        "TOTAL CREDIT AMOUNT": "CREDIT AMOUNT",
        "TOTAL CREDIT PAYMENTS": "CREDIT AMOUNT",
      };

      if (exactMap[upper]) {
        return exactMap[upper];
      }

      const stripped = upper.replace(/^TOTAL\s+/, "");
      if (stripped.startsWith("CREDIT")) {
        return "CREDIT AMOUNT";
      }
      if (stripped.endsWith(" PAYMENTS")) {
        return stripped;
      }
      const cleanName = stripped.replace(/\s*AMOUNT$/, "").replace(/\s*PAYMENT$/, "").replace(/\s*PAYMENTS$/, "");
      return `${cleanName} PAYMENTS`;
    },

    getPaymentAmount(inv, payType) {
      if (!inv) return 0;
      if (inv.payment_summary && inv.payment_summary[payType] !== undefined) {
        return this.toNumber(inv.payment_summary[payType]);
      }
      return 0;
    },

    getTotalForPaymentType(payType) {
      if (!Array.isArray(this.reportData)) return 0;
      return this.reportData.reduce((sum, inv) => {
        return sum + this.getPaymentAmount(inv, payType);
      }, 0);
    },

    getInvoiceTotal(inv) {
      if (!inv) return 0;
      if (inv.gross_amount !== undefined && inv.gross_amount !== null && inv.gross_amount !== "") {
        return this.toNumber(inv.gross_amount);
      }
      if (inv.gross_total !== undefined && inv.gross_total !== null && inv.gross_total !== "") {
        return this.toNumber(inv.gross_total);
      }
      return 0;
    },

    exportToExcel() {
      if (!this.reportData.length) {
        this.toast("error", "No data available to export");
        return;
      }

      try {
        const totalCols = 3 + this.uniqueItems.length + this.paymentTypes.length + 1;
        const padTo = (arr) => {
          const out = Array.isArray(arr) ? [...arr] : [];
          while (out.length < totalCols) out.push("");
          return out.slice(0, totalCols);
        };

        // ---- FILTERS (Top Metadata) ----
        let distributorName = "All Distributors";
        if (this.filters.distributor) {
          const selectedDist = this.distributors.find(
            d => String(d.profile_id ?? d.id) === String(this.filters.distributor)
          );
          if (selectedDist) {
            distributorName =
              selectedDist.name ||
              selectedDist.first_name ||
              selectedDist.full_name ||
              `Distributor #${this.filters.distributor}`;
          }
        }

        let vehicleName = "All Invoices";
        if (this.filters.vehicle === "all_vehicles") {
          vehicleName = "All Vehicle Invoices";
        } else if (this.filters.vehicle === "direct") {
          vehicleName = "All Direct Invoices";
        } else if (this.filters.vehicle === "all" || this.filters.vehicle === "0") {
          vehicleName = "All Invoices";
        } else if (this.filters.vehicle) {
          const selectedVehicle = this.vehicles.find(
            v => String(v.id) === String(this.filters.vehicle)
          );
          if (selectedVehicle) {
            vehicleName = selectedVehicle.vehicle_no || `Vehicle #${this.filters.vehicle}`;
          }
        }

        const filterRows = [
          ["Day End Report"],
          [],
          ["Distributor", distributorName],
          ["Vehicle",     vehicleName],
          ["Date",        this.filters.date || "-"],
          [],
        ];

        // ---- HEADERS ----
        const headerRow = [
          "#",
          "DEALER NAME",
          "INVOICE NO",
          ...this.uniqueItems.map(item => String(item.name).toUpperCase()),
          ...this.paymentTypes.map(payType => this.formatPaymentHeader(payType)),
          "TOTAL INVOICE AMOUNT",
        ];

        // ---- DATA ROWS ----
        const dataRows = this.reportData.map((row, index) => {
          const rowData = [
            index + 1,
            row.dealer_name  || "",
            row.invoices_no || row.invoice_no || "",
          ];

          this.uniqueItems.forEach(item => {
            rowData.push(this.getItemQuantity(row, item.name));
          });

          this.paymentTypes.forEach(payType => {
            rowData.push(this.getPaymentAmount(row, payType));
          });

          rowData.push(this.getInvoiceTotal(row));

          return rowData;
        });

        // ---- TOTALS ROW ----
        const totalsRow = ["", "", "TOTALS"];

        this.uniqueItems.forEach(item => {
          totalsRow.push(this.getTotalForItem(item.name));
        });

        this.paymentTypes.forEach(payType => {
          totalsRow.push(this.getTotalForPaymentType(payType));
        });

        totalsRow.push(this.totalInvoiceAmount);

        // ---- FOOTER (Bottom Metadata) ----
        const firstName    = localStorage.getItem("first_name") || "";
        const lastName     = localStorage.getItem("last_name")  || "";
        const userFullName = (firstName + " " + lastName).trim() || "-";
        const generatedAt  = new Date().toLocaleString("en-US", { hour12: false });

        const footerRows = [
          [],
          ["Generated by", userFullName],
          ["Generated at", generatedAt],
        ];

        // ---- BUILD FINAL AOA ----
        const aoa = [
          ...filterRows.map(padTo),
          padTo(headerRow),
          ...dataRows.map(padTo),
          padTo(totalsRow),
          ...footerRows.map(padTo),
        ];

        const ws = XLSX.utils.aoa_to_sheet(aoa);

        // ---- MERGES ----
        const merges = [];
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(3, totalCols - 1) } });
        ws["!merges"] = merges;

        // Make title bold
        if (ws["A1"]) ws["A1"].s = { font: { bold: true, sz: 14 } };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Day_End_Report");
        XLSX.writeFile(wb, `Day_End_Report_${this.filters.date || "export"}.xlsx`);

        this.toast("success", "Report exported successfully");
      } catch (error) {
        console.error("Export error:", error);
        this.toast("error", "Failed to export report. Please try again.");
      }
    },
  }
};
</script>

<style scoped>
.card {
  border-radius: 10px;
}

/* Filters and Actions Layout */
.dropdown-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
  margin-bottom: 20px;
  align-items: end;
}

.dropdown-item {
  width: 100%;
}

.dropdown-item.action-item {
  display: flex;
  align-items: flex-end;
}

.dropdown-item.action-item .btn {
  width: 100%;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}

select,
input[type="date"] {
  width: 100%;
  height: 40px;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  box-sizing: border-box;
  background-color: #fff;
}

select:focus,
input[type="date"]:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
}

select:disabled,
input[type="date"]:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

/* Default dropdown options (prevent inheriting inactive-select colors) */
.dropdown-item select option {
  background-color: #fff;
  color: #333;
}

/* Inactive vehicle: dropdown options */
.dropdown-item select option.inactive-option {
  background-color: #fde8e8;
  color: #b42318;
}

/* Select box when an inactive vehicle is the selected value */
.dropdown-item select.inactive-select {
  background-color: #fde8e8;
  color: #b42318 !important;
  border-color: #f1aeb5;
}

/* Button styles matching dealer VAT report */
.btn {
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.btn.button-default {
  background-color: #007bff;
  color: #fff;
}

.btn.button-default:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn.button-export {
  background-color: #28a745;
  color: #fff;
}

.btn.button-export:hover:not(:disabled) {
  background-color: #218838;
}

.btn:disabled {
  background-color: #6c757d !important;
  color: #fff !important;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Medium devices (tablets): 2 columns */
@media (min-width: 576px) and (max-width: 1023px) {
  .dropdown-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}

/* Max length devices (desktop / wide screens): single line */
@media (min-width: 1024px) {
  .dropdown-container {
    grid-template-columns: minmax(180px, 2fr) minmax(140px, 1.2fr) minmax(180px, 2fr) auto auto;
    gap: 10px;
    align-items: end;
  }

  .dropdown-item {
    width: auto;
  }

  .dropdown-item.action-item .btn {
    width: auto;
  }
}

/* Table Layout matching invoice summary (no column borders) */
.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 20px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #ddd;
  position: relative;
  z-index: 0;
}

.table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

.table th,
.table td {
  padding: 12px 20px;
  border-bottom: 1px solid #ddd;
  border-top: none;
  border-left: none !important;
  border-right: none !important;
  white-space: nowrap;
  vertical-align: middle;
  min-width: 100px;
  font-size: 0.85rem;
}

.table thead th {
  background-color: #f8f9fa !important;
  color: #333 !important;
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: bold;
  text-transform: uppercase;
  border-bottom: 1px solid #ddd !important;
  border-top: none !important;
  box-shadow: none !important;
}

.table tbody tr:hover {
  background-color: #f9f9f9;
}

/* Inactive Dealer Row - lite red background */
.table tr.inactive-dealer-row,
.table tr.inactive-dealer-row td {
  background-color: #ffebee !important;
}

.table tr.inactive-dealer-row:hover,
.table tr.inactive-dealer-row:hover td {
  background-color: #ffdcdb !important;
}

.table tfoot {
  font-weight: bold;
  background-color: #f1f1f1 !important;
}

.table tfoot td {
  background-color: #f1f1f1 !important;
  border-top: 1px solid #ddd;
  border-bottom: none;
  font-weight: bold;
}

@media print {
  .btn,
  .dropdown-container {
    display: none !important;
  }

  .table {
    font-size: 0.75rem;
  }
}

/* =========================================================
   LOADING STATE (Matching Dealer VAT Report)
========================================================= */
.loading-container {
  width: 100%;
}

.loading-card {
  text-align: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 42px 24px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.loading-spinner-wrapper {
  margin-bottom: 12px;
}

.loading-icon {
  font-size: 32px;
  color: #007bff;
}

.loading-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.loading-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0;
}

/* =========================================================
   EMPTY STATE (Matching Dealer VAT Report)
========================================================= */
.no-data-container {
  width: 100%;
  margin-top: 24px;
}

.no-data-card {
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 48px 32px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
}

.no-data-card:hover {
  border-color: #94a3b8;
  background: #f1f5f9;
}

.no-data-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px auto;
  color: #64748b;
  font-size: 32px;
  transition: transform 0.2s ease;
}

.no-data-card:hover .no-data-icon-wrapper {
  transform: scale(1.05);
  background: #dbeafe;
  color: #2563eb;
}

.no-data-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  max-width: 640px;
  margin: 0 auto 16px auto;
  line-height: 1.5;
}
</style>